import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import Header from './components/Layout/Header';
import WidgetCustomizer from './components/WidgetCustomization/WidgetCustomizer';
import PlayerBuilder from './pages/PlayerBuilder';
import SongHistory from './pages/SongHistory';
import Calendar from './pages/Calendar';
import { ThemeProvider } from 'styled-components';
import theme from './styles/theme';
import GlobalStyles from './styles/GlobalStyles';
import ImplementationGuide from './pages/ImplementationGuide';

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background: ${props => props.theme.colors.background.secondary};
  overflow-x: hidden;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: auto;
  width: 100%;
  box-sizing: border-box;

  /* Mobile screens */
  @media (max-width: 480px) {
    flex-direction: column;
    padding: 0.5rem;
  }

  /* Tablet screens */
  @media (min-width: 481px) and (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
  }

  /* Laptop screens */
  @media (min-width: 769px) and (max-width: 1024px) {
    flex-direction: row;
    padding: 1rem;
  }

  /* Wide screens */
  @media (min-width: 1025px) {
    flex-direction: row;
    padding: 1.5rem;
  }
`;

const BuilderContent = styled.div`
  display: flex;
  flex: 1;
  gap: 2rem;
  width: 100%;
  box-sizing: border-box;

  /* Mobile screens */
  @media (max-width: 480px) {
    flex-direction: column;
    padding: 0.5rem;
    gap: 0.5rem;
  }

  /* Tablet screens */
  @media (min-width: 481px) and (max-width: 768px) {
    flex-direction: column;
    padding: 0.5rem;
    gap: 1rem;
  }

  /* Laptop screens */
  @media (min-width: 769px) and (max-width: 1024px) {
    padding: 0.5rem;
    gap: 1rem;
  }

  /* Wide screens */
  @media (min-width: 1025px) {
    padding: 1rem;
    gap: 2rem;
  }
`;

const CustomizeSection = styled.div`
  flex: 0 0 auto;
  width: 375px;
  background: #fff;
  border-radius: 8px;
  overflow-y: auto;

  /* Mobile screens */
  @media (max-width: 480px) {
    width: 100%;
    min-width: unset;
    max-height: 40vh;
  }

  /* Tablet screens */
  @media (min-width: 481px) and (max-width: 768px) {
    width: 100%;
    min-width: unset;
    max-height: 45vh;
  }

  /* Laptop screens */
  @media (min-width: 769px) and (max-width: 1024px) {
    width: 320px;
    min-width: unset;
  }

  /* Wide screens */
  @media (min-width: 1025px) {
    width: 375px;
    min-width: unset;
  }
`;

const PreviewSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* Prevents flex child from overflowing */
  overflow-x: hidden;

  /* Mobile and Tablet screens */
  @media (max-width: 768px) {
    width: 100%;
    margin-top: 1rem;
  }

  /* Laptop and Wide screens */
  @media (min-width: 769px) {
    margin-left: 1rem;
  }
`;

const PageContent = styled.div`
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  overflow: auto;
  background: #fff;
  border-radius: 8px;

  /* Mobile screens */
  @media (max-width: 480px) {
    margin: 0.5rem;
    padding: 0.5rem;
  }

  /* Tablet screens */
  @media (min-width: 481px) and (max-width: 768px) {
    margin: 0.75rem;
    padding: 0.75rem;
  }

  /* Laptop screens */
  @media (min-width: 769px) and (max-width: 1024px) {
    margin: 1rem;
    padding: 1rem;
  }

  /* Wide screens */
  @media (min-width: 1025px) {
    margin: 1.5rem;
    padding: 1.5rem;
  }
`;

function App() {
  const [widgetState, setWidgetState] = useState({
    style: 'vertical',
    playerType: 'widget',
    device: 'desktop',
    textAlign: 'left',
    albumArt: 'contain',
    colors: {
      background: '#ECECEC',
      text: '#69ed86',
      buttonBackground: '#ECECEC',
      icon: '#69ed86',
      nowPlaying: '#ECECEC',
      liveIndicator: '#69ed86',
      timeBar: '#ECECEC',
      timeText: '#69ed86',
      historyModalBg: '#ECECEC',
      historyModalTexts: '#69ed86',
      backgroundShadow: '#1e1e1e',
      buttonShadow: '#1e1e1e'
    },
    settings: {
      volumeControl: true,
      songHistory: true,
      songName: true,
      artistName: true,
      time: true,
      timeBar: true,
      radioName: true,
      liveIndicator: true
    },
    urls: {
      streamUrl: '',
      apiUrl: ''
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Router>
        <AppContainer>
          <Header />
          <MainContent>
            <Routes>
              <Route path="/" element={<Navigate to="/player" replace />} />
              <Route
                path="/player"
                element={
                  <BuilderContent>
                    <CustomizeSection>
                      <WidgetCustomizer onStateChange={setWidgetState} />
                    </CustomizeSection>
                    <PreviewSection>
                      <PlayerBuilder widgetState={widgetState} />
                    </PreviewSection>
                  </BuilderContent>
                }
              />
              <Route path="/song-history" element={<PageContent><SongHistory /></PageContent>} />
              <Route path="/calendar" element={<PageContent><Calendar /></PageContent>} />
              <Route path="/docs/implementation-guide" element={<PageContent><ImplementationGuide /></PageContent>} />
            </Routes>
          </MainContent>
        </AppContainer>
      </Router>
    </ThemeProvider>
  );
}

export default App;
